import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowRight, Building2, GraduationCap, ShieldCheck, UserRound } from 'lucide-react';
import { motion } from 'motion/react';
import QRCode from 'qrcode';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { api, ApiError } from '../lib/api';
import type { Me } from '../lib/types';
import { Button, Card, Field, Logo, Notice } from '../ui';

type Mode = 'student' | 'staff';
type StaffStep = { step: 'password' } | { step: 'mfa'; setup?: { secret: string; otpauthUrl: string } } | { step: 'context'; contexts: Extract<Me, { stage: 'ready' | 'choose_context' }>['contexts'] };

const ROLE_LABEL: Record<string, string> = {
  teacher: 'Teacher',
  principal: 'Principal',
  lab_admin: 'Lab admin',
  platform_admin: 'Platform admin',
};

function BrandPanel() {
  return (
    <div className="relative hidden overflow-hidden bg-ink text-white lg:flex lg:flex-col lg:justify-between lg:p-12">
      <img src="/brand/logo-reverse.png" alt="AI4Skool" className="relative z-10 h-9 w-auto self-start" />
      <div className="relative z-10 max-w-md">
        <h1 className="text-4xl font-extrabold leading-tight">
          Watch. Play. Show what you know.
        </h1>
        <p className="mt-4 text-lg text-white/70">
          Computational thinking and AI lessons for CBSE classes, built for the school computer lab.
        </p>
      </div>
      <p className="relative z-10 text-sm text-white/50">Your school manages your account.</p>
      {/* Quiet geometric motif from the logo: orbit rings with gold nodes */}
      <svg className="absolute -right-24 top-1/2 w-[560px] -translate-y-1/2 opacity-60" viewBox="0 0 400 400" aria-hidden>
        <ellipse cx="200" cy="200" rx="180" ry="80" fill="none" stroke="#f2a93b" strokeOpacity=".35" strokeWidth="2" transform="rotate(30 200 200)" />
        <ellipse cx="200" cy="200" rx="180" ry="80" fill="none" stroke="#f2a93b" strokeOpacity=".35" strokeWidth="2" transform="rotate(-30 200 200)" />
        <circle cx="46" cy="268" r="8" fill="#f2a93b" />
        <circle cx="354" cy="268" r="8" fill="#f2a93b" />
        <circle cx="200" cy="200" r="54" fill="#5b47e0" fillOpacity=".5" />
      </svg>
    </div>
  );
}

export function SignInPage() {
  const [mode, setMode] = useState<Mode>('student');
  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.05fr_1fr]">
      <BrandPanel />
      <main className="flex items-center justify-center px-4 py-10 sm:px-8">
        <div className="w-full max-w-md">
          <Logo className="mb-8 h-8 lg:hidden" />
          <h2 className="text-3xl font-extrabold text-ink">Sign in</h2>
          <p className="mt-1.5 text-muted">Choose who is signing in.</p>

          <div role="tablist" aria-label="Who is signing in" className="mt-6 grid grid-cols-2 gap-2 rounded-2xl bg-line/60 p-1.5">
            {([
              ['student', 'Student', GraduationCap],
              ['staff', 'School staff', Building2],
            ] as const).map(([value, label, Icon]) => (
              <button
                key={value}
                role="tab"
                aria-selected={mode === value}
                onClick={() => setMode(value)}
                className={clsx(
                  'relative flex h-11 items-center justify-center gap-2 rounded-xl text-[15px] font-semibold transition-colors',
                  mode === value ? 'text-ink' : 'text-muted hover:text-ink',
                )}
              >
                {mode === value && (
                  <motion.span layoutId="signin-tab" className="absolute inset-0 rounded-xl bg-surface shadow-sm" transition={{ type: 'spring', stiffness: 500, damping: 40 }} />
                )}
                <Icon className="relative size-4" aria-hidden />
                <span className="relative">{label}</span>
              </button>
            ))}
          </div>

          <Card className="mt-5 p-6 sm:p-7">{mode === 'student' ? <StudentForm /> : <StaffForm />}</Card>
          <p className="mt-6 text-center text-sm text-muted">
            On a shared computer? Always sign out when you finish.
          </p>
        </div>
      </main>
    </div>
  );
}

function StudentForm() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [schoolCode, setSchoolCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await api.post('/auth/student/login', { schoolCode, username, password });
      await qc.invalidateQueries({ queryKey: ['me'] });
      navigate('/learn', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <Field label="School code" value={schoolCode} onChange={(e) => setSchoolCode(e.target.value.toUpperCase())} autoComplete="organization" placeholder="e.g. DEMO-SCHOOL-01" required />
      <Field label="Username" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" autoCapitalize="none" spellCheck={false} placeholder="From your teacher" required />
      <Field
        label="Password"
        type={show ? 'text' : 'password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password"
        required
        trailing={
          <button type="button" onClick={() => setShow((s) => !s)} className="rounded-lg px-2 py-1 text-sm font-semibold text-brand hover:bg-brand-soft">
            {show ? 'Hide' : 'Show'}
          </button>
        }
      />
      {error && <Notice tone="error">{error}</Notice>}
      <Button type="submit" size="lg" loading={busy} disabled={!schoolCode || !username || !password} icon={<UserRound className="size-5" aria-hidden />}>
        Start learning
      </Button>
      <p className="text-center text-sm text-muted">Forgot your password? Ask your teacher.</p>
    </form>
  );
}

function StaffForm() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [state, setState] = useState<StaffStep>({ step: 'password' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState('');

  useEffect(() => {
    if (state.step === 'mfa' && state.setup) QRCode.toDataURL(state.setup.otpauthUrl, { margin: 1, width: 180 }).then(setQr);
  }, [state]);

  const run = async (fn: () => Promise<void>) => {
    setError('');
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    await qc.invalidateQueries({ queryKey: ['me'] });
    navigate('/staff', { replace: true });
  };

  if (state.step === 'password') {
    return (
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            const r = await api.post<{ next: 'mfa' | 'mfa_setup'; mfa?: { secret: string; otpauthUrl: string } }>('/auth/staff/login', { email, password });
            setState({ step: 'mfa', setup: r.next === 'mfa_setup' ? r.mfa : undefined });
          });
        }}
      >
        <Field label="School email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
        <Field label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
        {error && <Notice tone="error">{error}</Notice>}
        <Button type="submit" size="lg" loading={busy} disabled={!email || !password} icon={<ArrowRight className="size-5" aria-hidden />}>
          Continue
        </Button>
      </form>
    );
  }

  if (state.step === 'mfa') {
    return (
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            const r = await api.post<{ contexts: Extract<StaffStep, { step: 'context' }>['contexts']; active: unknown }>('/auth/staff/mfa', { code });
            if (r.active) return finish();
            setState({ step: 'context', contexts: r.contexts });
          });
        }}
      >
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 size-6 shrink-0 text-brand" aria-hidden />
          <div>
            <p className="font-display text-lg font-bold">{state.setup ? 'Set up two-step sign-in' : 'Enter your code'}</p>
            <p className="text-sm text-muted">
              {state.setup
                ? 'Scan this with Google Authenticator or Microsoft Authenticator, then enter the 6-digit code.'
                : 'Open your authenticator app and enter the 6-digit code for AI4Skool.'}
            </p>
          </div>
        </div>
        {state.setup && (
          <div className="flex flex-col items-center gap-2 rounded-xl bg-canvas-staff p-4">
            {qr ? <img src={qr} alt="QR code for your authenticator app" className="size-44" /> : <div className="size-44" />}
            <code className="select-all break-all text-center text-xs text-muted">{state.setup.secret}</code>
          </div>
        )}
        <Field label="6-digit code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required />
        {error && <Notice tone="error">{error}</Notice>}
        <Button type="submit" size="lg" loading={busy} disabled={code.length !== 6}>
          Verify
        </Button>
        <Button type="button" variant="ghost" onClick={() => { setState({ step: 'password' }); setCode(''); }}>
          Back
        </Button>
      </form>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="font-display text-lg font-bold">Choose where to work</p>
      {state.contexts.flatMap((c) =>
        c.roles.map((role) => (
          <button
            key={`${c.tenantId}-${role}`}
            disabled={busy}
            onClick={() => void run(async () => { await api.post('/auth/context', { tenantId: c.tenantId, role }); await finish(); })}
            className="flex items-center justify-between rounded-xl border-2 border-line px-4 py-3 text-left transition-colors hover:border-brand"
          >
            <span>
              <span className="block font-semibold">{c.tenantName}</span>
              <span className="text-sm text-muted">{ROLE_LABEL[role] ?? role}</span>
            </span>
            <ArrowRight className="size-5 text-brand" aria-hidden />
          </button>
        )),
      )}
      {error && <Notice tone="error">{error}</Notice>}
    </div>
  );
}
